import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useDeviceInfo } from "@/hooks/use-device-info";
import { useCompanyType } from "@/hooks/use-company-type";
import { useUser } from "@/contexts/UserContext";
import { useCompanyPreferences, resolveStaffNav } from "@/hooks/use-company-preferences";
import {
    ArrowDownRight,
    ArrowUpRight,
    BarChart2,
    ClipboardList,
    Home,
    LogOut,
    Menu,
    Package,
    Settings,
    Users,
    X,
    ShoppingCart,
    ArrowRight,
    ArrowLeft,
    TreePine,
    Shield,
    FlaskConical,
    Beaker,
    Zap,
    Trash2,
    Bot,
    UserRound,
    Truck,
    Handshake,
} from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface SidebarItemProps {
  icon: React.ElementType;
  title: string;
  path: string;
  active: boolean;
  collapsed: boolean;
}

const SidebarItem = ({ icon: Icon, title, path, active, collapsed }: SidebarItemProps) => {
  return (
    <Link to={path} className="block">
      <Button
        variant="ghost"
        className={cn(
          "w-full justify-start mb-1 px-4 cursor-pointer",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          active ? "bg-primary text-primary-foreground font-medium" : "text-sidebar-foreground",
          collapsed ? "py-3" : "py-2"
        )}
      >
        <Icon className={cn("h-5 w-5 flex-shrink-0", collapsed ? "mx-auto" : "mr-3")} />
        {!collapsed && <span className="truncate">{title}</span>}
      </Button>
    </Link>
  );
};

/** A row in the sidebar menu. */
type NavItem = { title: string; path: string; icon: React.ElementType };

/**
 * Referrals sits late in the menu — straight after AI Agents, before Settings —
 * rather than up top with the admin pages. Every signed-in account gets the
 * link, admin or staff; the referral area itself decides what a referrer may
 * do (joining is open to anyone with an account).
 */
const withReferrals = (items: NavItem[]): NavItem[] => {
  const at = items.findIndex((item) => item.title === "AI Agents");
  if (at === -1) return items;
  return [
    ...items.slice(0, at + 1),
    { title: "Referrals", path: "/referrals/dashboard", icon: Handshake },
    ...items.slice(at + 1),
  ];
};

export function Sidebar() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const { setIsAuthenticated } = useAuth();
  const { isMobile } = useDeviceInfo();
  const { companyType, isTimberCompany, isLiquidCompany, isManufacturingCompany } = useCompanyType();
  const { user, isAdmin, isSystemAdmin, loading } = useUser();
  const { preferences } = useCompanyPreferences();

  // Hide sidebar on mobile devices
  if (isMobile) {
    return null;
  }

  // System admin access is decided by the backend (SYSTEM_ADMIN_EMAILS) and
  // arrives on the user profile, so the menu does not keep its own list.
  
  // Define different navigation items based on company type
  const getNavigationItems = () => {
    const baseItems: NavItem[] = [];
    
    // Add system admin dashboard for system admins
    if (isSystemAdmin) {
      baseItems.push({ title: "System Admin", path: "/system-admin", icon: Shield });
      baseItems.push({ title: "Referral Admin", path: "/admin/referrals", icon: Handshake });
    }
    
    // Only add admin dashboard for admin users
    if (isAdmin) {
      baseItems.push({ title: "Admin Dashboard", path: "/admin", icon: Shield });
    }

    if (isTimberCompany) {
      return withReferrals([
        ...baseItems,
        { title: "Dashboard", path: "/", icon: Home },
        { title: "Sales Orders", path: "/orders/sales", icon: ArrowRight },
        { title: "Website Orders", path: "/orders", icon: ClipboardList },
        { title: "Timber Inventory", path: "/timber", icon: TreePine },
        { title: "Production Top-Up", path: "/timber/production", icon: Package },
        { title: "Timber Analytics", path: "/timber-analytics", icon: BarChart2 },
        { title: "Suppliers", path: "/suppliers", icon: Truck },
        { title: "Customers", path: "/customers", icon: UserRound },
        { title: "AI Agents", path: "/agents", icon: Bot },
        { title: "Settings", path: "/settings", icon: Settings },
      ]);
    } else if (isLiquidCompany || isManufacturingCompany) {
      return withReferrals([
        ...baseItems,
        { title: "Dashboard", path: "/", icon: Home },
        { title: "Sales Orders", path: "/orders/sales", icon: ArrowRight },
        { title: "Website Orders", path: "/orders", icon: ClipboardList },
        { title: "Inventory", path: "/inventory", icon: Package },
        { title: "Quick Sale", path: "/production/quick-sale", icon: Zap },
        { title: "Formulas", path: "/production/formulas", icon: FlaskConical },
        { title: "Production", path: "/production/run", icon: Beaker },
        { title: "Waste", path: "/waste", icon: Trash2 },
        { title: "Accounting", path: "/accounting", icon: BarChart2 },
        { title: "Suppliers", path: "/suppliers", icon: Truck },
        { title: "Customers", path: "/customers", icon: UserRound },
        { title: "Credit Customers", path: "/credit-customers", icon: Users },
        { title: "AI Agents", path: "/agents", icon: Bot },
        { title: "Settings", path: "/settings", icon: Settings },
      ]);
    } else {
      return withReferrals([
        ...baseItems,
        { title: "Dashboard", path: "/", icon: Home },
        { title: "Sales Orders", path: "/orders/sales", icon: ArrowRight },
        { title: "Website Orders", path: "/orders", icon: ClipboardList },
        { title: "Inventory", path: "/inventory", icon: Package },
        { title: "Suppliers", path: "/suppliers", icon: Truck },
        { title: "Customers", path: "/customers", icon: UserRound },
        { title: "Credit Customers", path: "/credit-customers", icon: Users },
        { title: "AI Agents", path: "/agents", icon: Bot },
        { title: "Settings", path: "/settings", icon: Settings },
      ]);
    }
  };

  const sidebarItems = getNavigationItems();

  // While loading, show base nav items without admin-specific ones
  // This prevents the empty sidebar flash on first load
  const getBaseItems = () => {
    if (isTimberCompany) {
      return withReferrals([
        { title: "Dashboard", path: "/", icon: Home },
        { title: "Sales Orders", path: "/orders/sales", icon: ArrowRight },
        { title: "Website Orders", path: "/orders", icon: ClipboardList },
        { title: "Timber Inventory", path: "/timber", icon: TreePine },
        { title: "Production Top-Up", path: "/timber/production", icon: Package },
        { title: "Timber Analytics", path: "/timber-analytics", icon: BarChart2 },
        { title: "Suppliers", path: "/suppliers", icon: Truck },
        { title: "Customers", path: "/customers", icon: UserRound },
        { title: "AI Agents", path: "/agents", icon: Bot },
        { title: "Settings", path: "/settings", icon: Settings },
      ]);
    }
    if (isLiquidCompany || isManufacturingCompany) {
      return withReferrals([
        { title: "Dashboard", path: "/", icon: Home },
        { title: "Sales Orders", path: "/orders/sales", icon: ArrowRight },
        { title: "Website Orders", path: "/orders", icon: ClipboardList },
        { title: "Inventory", path: "/inventory", icon: Package },
        { title: "Quick Sale", path: "/production/quick-sale", icon: Zap },
        { title: "Formulas", path: "/production/formulas", icon: FlaskConical },
        { title: "Production", path: "/production/run", icon: Beaker },
        { title: "Waste", path: "/waste", icon: Trash2 },
        { title: "Accounting", path: "/accounting", icon: BarChart2 },
        { title: "Suppliers", path: "/suppliers", icon: Truck },
        { title: "Customers", path: "/customers", icon: UserRound },
        { title: "Credit Customers", path: "/credit-customers", icon: Users },
        { title: "AI Agents", path: "/agents", icon: Bot },
        { title: "Settings", path: "/settings", icon: Settings },
      ]);
    }
    return withReferrals([
      { title: "Dashboard", path: "/", icon: Home },
      { title: "Sales Orders", path: "/orders/sales", icon: ArrowRight },
      { title: "Website Orders", path: "/orders", icon: ClipboardList },
      { title: "Inventory", path: "/inventory", icon: Package },
      { title: "Suppliers", path: "/suppliers", icon: Truck },
      { title: "Credit Customers", path: "/credit-customers", icon: Users },
      { title: "AI Agents", path: "/agents", icon: Bot },
      { title: "Settings", path: "/settings", icon: Settings },
    ]);
  };

  const navItems = loading ? getBaseItems() : sidebarItems;

  // Filter nav by the pages granted to this user: per-user list (staff_nav_users)
  // wins, then the general staff_nav; null means unrestricted (admin / no grants).
  const allowedNav = resolveStaffNav(preferences, user, isAdmin);
  const visibleNavItems = allowedNav === null
    ? isAdmin
      ? navItems
      : navItems.filter(item => item.path !== "/agents") // staff never see AI Agents
    : navItems.filter(item => allowedNav.includes(item.path) || item.path === "/settings"); // Settings always stays — it shows staff their own branch/pages access

  const handleLogout = () => {
    localStorage.removeItem("token"); // Changed from "stockwise-token" to "token"
    setIsAuthenticated(false);
    navigate("/login");
  };

  return (
    <div 
      className={cn(
        "bg-sidebar flex flex-col border-r border-sidebar-border h-screen transition-[width] duration-300 ease-in-out",
        collapsed ? "w-[70px]" : "w-[250px]"
      )}
    >
      <div className="flex items-center p-4 border-b border-sidebar-border">          {!collapsed && (
            <div className="flex items-center">
              {/* White chip keeps the wordmark legible on the navy rail */}
              <img src="/stockwise-logo.png" alt="StockWise Logo" className="h-10 w-auto bg-white rounded-md px-1.5 py-1" />
            </div>
          )}
          {collapsed && (
            <div className="flex justify-center">
              <img src="/stockwise-logo.png" alt="StockWise Logo" className="h-8 w-8 object-contain bg-white rounded-md p-0.5" />
            </div>
          )}
        <Button 
          variant="ghost" 
          size="icon" 
          className={cn("ml-auto text-sidebar-foreground hover:text-sidebar-accent-foreground", collapsed && "mx-auto")}
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? <Menu className="h-5 w-5" /> : <X className="h-5 w-5" />}
        </Button>
      </div>
      
      <div className="flex-1 py-4 px-2 overflow-y-auto">
        <div className="space-y-1">
          {visibleNavItems.map((item) => (
            <SidebarItem
              key={item.path}
              icon={item.icon}
              title={item.title}
              path={item.path}
              active={item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path)}
              collapsed={collapsed}
            />
          ))}
        </div>
      </div>
      
      <div className="p-4 border-t border-sidebar-border space-y-1">
        <Button 
          variant="ghost" 
          className={cn("w-full justify-start text-sidebar-foreground", 
            collapsed ? "py-3" : "py-2"
          )}
          onClick={handleLogout}
        >
          <LogOut className={cn("h-5 w-5", collapsed ? "mx-auto" : "mr-3")} />
          {!collapsed && <span>Logout</span>}
        </Button>
      </div>
    </div>
  );
}
